/* eslint-disable no-param-reassign */
import Plan from 'Classes/Plan';
import { moment } from 'Helpers/DateTime';
import history from 'State/history';

const ESTIMATE_TYPE = {
  CURRENT: 'current',
  GOAL: 'goal',
};

const ACCOUNT_TYPE = {
  CASH: 'cash',
  COLLEGE: 'college',
  INVESTMENT: 'investment',
  UTMA: 'utma',
};

function convertRequest(plan) {
  const request = {
    id: plan.id,
    type: 'plan',
    attributes: {
      householdID: plan.householdID.toString(),
      type: plan.type,
      name: plan.planName,
      templates: plan.templates,
    },
  };
  return request;
}

function convertResponse(response) {
  const plan = new Plan();
  plan.id = response.id;
  // Our plan endpoints seem to return inconsistent data structures, so I have
  // to check for this.
  const data = response.attributes || (response.data && response.data.attributes);
  plan.name = data.name;
  plan.householdID = data.householdID;
  plan.planName = data.name;
  plan.lastModified = parseInt(moment(data.lastModified).format('x'), 10);
  plan.url = response.relationships
    && response.relationships.data
    && response.relationships.data.links
    && response.relationships.data.links.related;
  plan.iconSrc = response.relationships
    && response.relationships.icon
    && response.relationships.icon.links
    && response.relationships.icon.links.self;
  plan.planMessageRelationship = response.relationships?.planMessage?.links?.related;
  plan.type = data.type;
  if (response.type === 'externalPlan') {
    plan.externalPlanType = data.type;
    plan.type = 'external';
    plan.secureFileUploadURL = response.relationships
      && response.relationships.secureFileUpload
      && response.relationships.secureFileUpload.links
      && response.relationships.secureFileUpload.links.related;
    plan.secureFileURL = response.relationships
      && response.relationships.secureFile
      && response.relationships.secureFile.links
      && response.relationships.secureFile.links.related;
  }

  return plan;
}

function convertResourceToPlan(planResource, householdId) {
  const plan = new Plan();
  plan.id = planResource.id();
  plan.name = planResource.attr('name');
  plan.householdID = householdId;
  plan.planName = planResource.attr('name');
  plan.lastModified = parseInt(moment(planResource.attr('lastModified')).format('x'), 10);
  plan.type = planResource.attr('type');
  if (planResource.type() === 'externalPlan') {
    plan.url = planResource.relationship('data').link('related').url();
    plan.externalPlanType = planResource.attr('type');
    plan.type = 'external';
  }

  return plan;
}

function savePlanHandler(
  create,
  plan,
  set,
  update,
  next,
  onFailure,
  runCalculations,
  planToRequest,
  responseToPlan,
) {
  plan.current = runCalculations(plan, ESTIMATE_TYPE.CURRENT);
  plan.goal = runCalculations(plan, ESTIMATE_TYPE.GOAL);
  const request = planToRequest(plan);
  let save;
  if (plan.id > 0) {
    save = update(request);
  } else {
    save = create(request);
  }
  return save
    .then(({ data: resp }) => {
      let data = resp;

      if (Array.isArray(data) && data.length > 0) {
        const [singlePlan] = data;
        data = singlePlan;
      }

      const updatedPlan = responseToPlan(data);
      set(updatedPlan);
      switch (typeof next) {
        case 'function':
          next();
          break;
        default:
          history.push(next);
      }
      return data;
    })
    .catch((err) => {
      if (onFailure) onFailure(err);
    });
}

function parseAccount(section, attributes, accountType, estimateType) {
  const accountPrefix = `${estimateType}${accountType
    .substring(0, 1)
    .toUpperCase()}${accountType.substring(1)}`;
  section[accountType].description = accountType;
  section[accountType].balance = attributes[`${accountPrefix}Balance`] !== 0
    ? attributes[`${accountPrefix}Balance`] || ''
    : 0;
  section[accountType].annualContribution = attributes[`${accountPrefix}AnnualContribution`] !== 0
    ? attributes[`${accountPrefix}AnnualContribution`] || ''
    : 0;
  section[accountType].interestRate = attributes[`${accountPrefix}InterestRate`] !== 0
    ? attributes[`${accountPrefix}InterestRate`] || ''
    : 0;
}

export {
  ACCOUNT_TYPE,
  ESTIMATE_TYPE,
  convertRequest,
  convertResponse,
  parseAccount,
  savePlanHandler,
  convertResourceToPlan,
};
